'use client'

import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { BackButton } from '@/components/ui/BackButton'
import { useToast } from '@/components/ui/use-toast'
import { ErrorDisplay } from '@/components/ui/error-display'
import { useBulkCreateTaxExemptions } from '@/lib/api/taxExemptions'
import { parseTRPCError, type ParsedTRPCError } from '@/lib/utils/trpc-error-utils'
import { useStoreNavigate } from '@/hooks/use-app-navigate'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

const bulkCreateSchema = z.object({
	emailsText: z.string().min(1, 'At least one email is required'),
})

type BulkCreateFormData = z.infer<typeof bulkCreateSchema>

function parseEmails(text: string): string[] {
	return text
		.split(/[\n,;]+/)
		.map((email) => email.trim())
		.filter((email) => email.length > 0)
}

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(CreateTaxExemptionsPage, PERMISSIONS.STORES_MANAGE);

function CreateTaxExemptionsPage() {
	const { t } = useTranslation('taxes')
	const { t: tCommon } = useTranslation('common')
	const { storeRoutes, navigate, id: storeId } = useStoreNavigate()
	const { toast } = useToast()

	const bulkCreateMutation = useBulkCreateTaxExemptions()
	const [formError, setFormError] = React.useState<ParsedTRPCError | null>(null)

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
	} = useForm<BulkCreateFormData>({
		defaultValues: { emailsText: '' },
		resolver: zodResolver(bulkCreateSchema),
		mode: 'onBlur',
	})

	const handleCreate = async (data: BulkCreateFormData) => {
		try {
			setFormError(null)

			const emails = parseEmails(data.emailsText)

			if (emails.length === 0) {
				toast({
					title: tCommon('error'),
					description: t('exemptions.noValidEmails'),
					variant: 'destructive',
				})
				return
			}

			// Validate each email
			const emailSchema = z.string().email()
			const invalidEmails = emails.filter((email) => !emailSchema.safeParse(email).success)

			if (invalidEmails.length > 0) {
				toast({
					title: tCommon('error'),
					description: t('exemptions.invalidEmails', { emails: invalidEmails.join(', ') }),
					variant: 'destructive',
				})
				return
			}

			await bulkCreateMutation.mutateAsync({
				storeId,
				emails,
			})

			toast({
				title: tCommon('success'),
				description: t('exemptions.createSuccess', { count: emails.length }),
			})

			navigate(storeRoutes.TAX_EXEMPTIONS())
		} catch (error) {
			const parsedError = parseTRPCError(error)
			setFormError(parsedError)

			if (!parsedError.isValidationError) {
				toast({
					title: tCommon('error'),
					description: parsedError.message,
					variant: 'destructive',
				})
			}
		}
	}

	return (
		<div className="bg-background min-h-screen">
			<div className="flex flex-col space-y-4 sm:space-y-6">
				<form onSubmit={handleSubmit(handleCreate)}>
					{/* Header */}
					<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
						<div className="flex items-center gap-4">
							<BackButton to={storeRoutes.TAX_EXEMPTIONS()} />
							<div>
								<h1 className="text-2xl font-bold tracking-tight">{t('exemptions.createTitle')}</h1>
								<p className="text-muted-foreground mt-1 text-sm">{t('exemptions.createDescription')}</p>
							</div>
						</div>
						<div className="flex gap-2">
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={() => navigate(storeRoutes.TAX_EXEMPTIONS())}
								disabled={isSubmitting}
							>
								{tCommon('cancel')}
							</Button>
							<Button type="submit" size="sm" disabled={isSubmitting || bulkCreateMutation.isPending}>
								{isSubmitting || bulkCreateMutation.isPending
									? t('exemptions.creating')
									: t('exemptions.createExemptions')}
							</Button>
						</div>
					</div>

					{/* Error Display */}
					{formError && <ErrorDisplay error={formError} onDismiss={() => setFormError(null)} />}

					{/* Main Content */}
					<div className="mt-6">
						<Card>
							<CardHeader>
								<CardTitle>{t('exemptions.emailsTitle')}</CardTitle>
								<CardDescription>
									{t('exemptions.emailsDescription')}
								</CardDescription>
							</CardHeader>
							<CardContent>
								<div className="space-y-2">
									<Label htmlFor="emailsText">{t('exemptions.emailsLabel')}</Label>
									<Textarea
										id="emailsText"
										{...register('emailsText')}
										placeholder={t('exemptions.emailsPlaceholder')}
										rows={10}
										className="font-mono text-sm"
									/>
									{errors.emailsText && (
										<p className="text-destructive text-sm">{errors.emailsText.message}</p>
									)}
								</div>
							</CardContent>
						</Card>
					</div>
				</form>
			</div>
		</div>
	)
}
